import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { ArtifactCache } from '../global-ai-turn-messages';
import { buildFilterResolutionDigest } from '../turn-outcome-digest';

/**
 * Structural sniff for a TD v2 search_filters response — the only artifact
 * shape whose resolution we can judge.
 */
function looksLikeTdV2Response(
  artifact: unknown
): artifact is TalentDiscoveryAiFilterSearchResponseV2 {
  return (
    typeof artifact === 'object' &&
    artifact !== null &&
    'relevantPromptText' in artifact &&
    'filters' in artifact
  );
}

/**
 * Whether a screener `filters` tree holds any value: a list entry, a flag, a
 * number or text. Every field is nullable, so an all-null tree is the screener's
 * "successfully empty".
 */
function hasAnyFilterValue(value: unknown): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') {
    return Object.values(value).some(hasAnyFilterValue);
  }
  return value !== '';
}

/**
 * Gate a grounded-offer artifact on having actually RESOLVED something. The
 * resolver can return "successfully empty" — an expr with no children and the
 * named entity in `unknownFilters` — and applying that on the destination
 * would REPLACE the page's default filters with nothing, so an empty artifact
 * is treated as no artifact. A screener artifact counts as empty when its
 * `filters` tree holds no value; other shapes pass through unchanged.
 */
function groundedArtifactOrUndefined(artifact: unknown): unknown {
  if (!looksLikeTdV2Response(artifact)) {
    if (
      typeof artifact === 'object' &&
      artifact !== null &&
      'filters' in artifact &&
      !hasAnyFilterValue(artifact.filters)
    ) {
      return undefined;
    }
    return artifact ?? undefined;
  }
  return hasAppliedTdFilters(artifact) ? artifact : undefined;
}

function hasAppliedTdFilters(
  response: TalentDiscoveryAiFilterSearchResponseV2
): boolean {
  return (
    buildFilterResolutionDigest({ response, targetPage: '' }).applied.length > 0
  );
}

/**
 * The `filterArtifactId → grounded artifact` resolution every grounded-offer
 * tool performs (navigate, offer_page_or_custom, offer_report's page
 * ride-along). `filtersUnresolved` means the model MEANT the offer to carry
 * filters but nothing usable resolved. Each tool then creates no page link,
 * since one would open the page on its default filters, and tells the model
 * why.
 *
 * The asymmetry is deliberate: a FALSY id skips the lookup, while
 * `filtersUnresolved` tests `!== undefined` — so an empty-string id counts as
 * "meant to ground" and flags as unresolved.
 */
export function resolveGroundedOffer(
  artifacts: ArtifactCache,
  filterArtifactId: string | undefined
): { grounded: unknown; filtersUnresolved: boolean } {
  const grounded = filterArtifactId
    ? groundedArtifactOrUndefined(artifacts.get(filterArtifactId))
    : undefined;
  return {
    grounded,
    filtersUnresolved: filterArtifactId !== undefined && grounded === undefined,
  };
}

/** A page link needs a population that resolved; what did not resolve was disclosed with the answer. */
export function isResolvedPageOfferArtifact(
  artifact: unknown
): artifact is TalentDiscoveryAiFilterSearchResponseV2 {
  return looksLikeTdV2Response(artifact) && hasAppliedTdFilters(artifact);
}
